/**
 * HTTP client for the Flask proxy backend.
 * All fetch calls to the backend go through this module.
 * @module api/client
 */

import { getSsoToken } from "../office/auth";
import { wait } from "../utils/helpers";

/** Backend URL prefix (empty for relative URLs proxied via nginx/webpack) */
export const BACKEND = "";

/**
 * Builds authentication headers for API requests.
 * Uses SSO token if available, otherwise falls back to email/password headers.
 * @returns {object} Headers object
 */
export function getAuthHeaders() {
  const token = getSsoToken();
  if (token) {
    return { "Authorization": `Bearer ${token}` };
  }
  const headers = {};
  const email = document.getElementById("email-input").value.toLowerCase().trim();
  headers["X-User-Email"] = email;
  const pwdInput = document.getElementById("password-input");
  if (pwdInput && pwdInput.value) {
    headers["X-App-Password"] = pwdInput.value;
  }
  return headers;
}

/**
 * Fetches a URL with automatic retry on server errors (5xx, 429) and network failures.
 * Uses exponential backoff and respects Retry-After headers.
 * @param {string} url
 * @param {RequestInit} [options={}]
 * @param {number} [maxRetries=3]
 * @returns {Promise<Response>}
 */
export async function fetchWithRetry(url, options = {}, maxRetries = 3) {
  const delays = [1000, 2000, 4000];
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const res = await fetch(url, options);
      if (res.ok || (res.status < 500 && res.status !== 429)) return res;
      if (attempt === maxRetries) return res;
      const retryAfter = res.headers.get("Retry-After");
      const delay = retryAfter ? parseInt(retryAfter, 10) * 1000 : delays[attempt];
      await wait(delay);
    } catch (err) {
      // AbortError = annulation utilisateur → propager immédiatement sans retry
      if (err.name === "AbortError") throw err;
      if (attempt === maxRetries) throw err;
      await wait(delays[attempt]);
    }
  }
}
